import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { admissionService, dischargeSummaryService } from '../../lib/services.ipd';
import { generateDischargeSummaryPDF } from '../../lib/pdf';
import type { Admission } from '../../lib/services.ipd';
import toast from 'react-hot-toast';

interface DischargeSummaryFormData {
  admissionId: string;
  admissionDiagnosis: string;
  finalDiagnosis: string;
  treatmentSummary: string;
  proceduresPerformed: string;
  medicationsPrescribed: string;
  followUpInstructions: string;
  dietRestrictions: string;
  activityRestrictions: string;
  followUpDate: string;
}

export default function DischargeSummaryForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const admissionIdParam = searchParams.get('admissionId');
  
  const [admissions, setAdmissions] = useState<Admission[]>([]);
  const [selectedAdmission, setSelectedAdmission] = useState<Admission | null>(null);
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm<DischargeSummaryFormData>({
    defaultValues: {
      admissionId: admissionIdParam || ''
    }
  });

  useEffect(() => {
    loadAdmissions();
  }, []);

  useEffect(() => {
    if (admissionIdParam) {
      loadAdmissionDetails(admissionIdParam);
    }
  }, [admissionIdParam]);

  const loadAdmissions = async () => {
    try {
      const activeAdmissions = await admissionService.getActive();
      setAdmissions(activeAdmissions);
    } catch (error) {
      console.error('Failed to load admissions:', error);
      toast.error('Failed to load admissions');
    }
  };

  const loadAdmissionDetails = async (admissionId: string) => {
    try {
      const admission = await admissionService.getById(admissionId);
      if (admission) {
        setSelectedAdmission(admission);
        setValue('admissionId', admissionId);
        if (admission.provisional_diagnosis) {
          setValue('admissionDiagnosis', admission.provisional_diagnosis);
        }
      }
    } catch (error) {
      console.error('Failed to load admission details:', error);
    }
  };

  const onSubmit = async (data: DischargeSummaryFormData) => {
    if (!selectedAdmission) {
      toast.error('Please select an admission');
      return;
    }

    setLoading(true);
    try {
      const summary = await dischargeSummaryService.create({
        admission_id: data.admissionId,
        discharge_date: new Date().toISOString(),
        discharged_by: '',
        admission_diagnosis: data.admissionDiagnosis,
        final_diagnosis: data.finalDiagnosis,
        treatment_summary: data.treatmentSummary,
        procedures_performed: data.proceduresPerformed,
        medications_prescribed: data.medicationsPrescribed,
        follow_up_instructions: data.followUpInstructions,
        diet_restrictions: data.dietRestrictions,
        activity_restrictions: data.activityRestrictions,
        follow_up_date: data.followUpDate || undefined,
      });

      const pdfDoc = generateDischargeSummaryPDF({
        summaryNo: summary.summary_no,
        patient: selectedAdmission.patient,
        admission: {
          admission_date: selectedAdmission.admission_date,
          discharge_date: summary.discharge_date
        },
        doctor: selectedAdmission.doctor,
        admissionDiagnosis: data.admissionDiagnosis,
        finalDiagnosis: data.finalDiagnosis,
        treatmentSummary: data.treatmentSummary,
        proceduresPerformed: data.proceduresPerformed,
        medicationsPrescribed: data.medicationsPrescribed,
        followUpInstructions: data.followUpInstructions,
        dietRestrictions: data.dietRestrictions,
        activityRestrictions: data.activityRestrictions,
        followUpDate: data.followUpDate
      });

      const pdfBlob = pdfDoc.output('blob');
      await dischargeSummaryService.uploadPDF(summary.id, pdfBlob);

      toast.success('Discharge summary created successfully');
      navigate(`/ipd/${data.admissionId}`);
    } catch (error) {
      console.error('Failed to create discharge summary:', error);
      toast.error('Failed to create discharge summary');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/ipd')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Admissions
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Create Discharge Summary</h1>
        <p className="text-gray-600">Complete discharge documentation for patient</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-4xl">
        <div className="bg-white rounded-lg shadow-sm p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Admission <span className="text-red-500">*</span>
            </label>
            <select
              {...register('admissionId', { required: 'Admission is required' })}
              onChange={(e) => loadAdmissionDetails(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select an admission</option>
              {admissions.map((admission) => (
                <option key={admission.id} value={admission.id}>
                  {admission.admission_no} - {admission.patient?.name} (Admitted: {new Date(admission.admission_date).toLocaleDateString()})
                </option>
              ))}
            </select>
            {errors.admissionId && (
              <p className="text-sm text-red-600 mt-1">{errors.admissionId.message}</p>
            )}
          </div>

          {selectedAdmission && (
            <>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Patient Information</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Name:</span> {selectedAdmission.patient?.name}
                  </div>
                  <div>
                    <span className="text-gray-600">Doctor:</span> {selectedAdmission.doctor?.name}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admission Diagnosis <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('admissionDiagnosis', { required: 'Admission diagnosis is required' })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Initial diagnosis at admission"
                />
                {errors.admissionDiagnosis && (
                  <p className="text-sm text-red-600 mt-1">{errors.admissionDiagnosis.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Final Diagnosis <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('finalDiagnosis', { required: 'Final diagnosis is required' })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Final diagnosis at discharge"
                />
                {errors.finalDiagnosis && (
                  <p className="text-sm text-red-600 mt-1">{errors.finalDiagnosis.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Treatment Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('treatmentSummary', { required: 'Treatment summary is required' })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Summary of treatment provided during hospitalization"
                />
                {errors.treatmentSummary && (
                  <p className="text-sm text-red-600 mt-1">{errors.treatmentSummary.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Procedures Performed
                </label>
                <textarea
                  {...register('proceduresPerformed')}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="List of procedures performed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Medications Prescribed <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('medicationsPrescribed', { required: 'Medications are required' })}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="List of medications with dosage and duration"
                />
                {errors.medicationsPrescribed && (
                  <p className="text-sm text-red-600 mt-1">{errors.medicationsPrescribed.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Follow-up Instructions <span className="text-red-500">*</span>
                </label>
                <textarea
                  {...register('followUpInstructions', { required: 'Follow-up instructions are required' })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Instructions for patient follow-up care"
                />
                {errors.followUpInstructions && (
                  <p className="text-sm text-red-600 mt-1">{errors.followUpInstructions.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Diet Restrictions
                  </label>
                  <textarea
                    {...register('dietRestrictions')}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Dietary guidelines"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Activity Restrictions
                  </label>
                  <textarea
                    {...register('activityRestrictions')}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Activity limitations"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Follow-up Date
                </label>
                <input
                  type="date"
                  {...register('followUpDate')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => navigate('/ipd')}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {loading ? 'Creating...' : 'Create Discharge Summary'}
                </button>
              </div>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
